import React, { createContext, useContext, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance, { handleApiError } from "../services/axiosService";
import { updateFormChecksum } from "../utils/configChecksum/setterFunctions/formConfig";
import { useAppBlocksContext } from "./AppBlocksContext";
import { useTreeContext } from "./TreeDataContext";
import { getElementIds } from "../Pages/FlexibleLayout/utils";
import { getTreeDataForForm, getTreeDataForRdbmsForm } from "../utils/treeData/treeDataUtils";
import { processLayout } from "../utils/GridLayoutFunc";

const FormContextApi = createContext();
const useFormContext = () => useContext(FormContextApi);

const FormContext = ({ children }) => {
  const [selectedForm, setSelectedForm] = useState({ id: "", name: "", parentData: "" });

  const [renderFieldsArray, setRenderFieldsArray] = useState([]);
  const [selectedSectionData, setSelectedSectionData] = useState(0);
  const formSectionsData = useRef([]);
  const formSettingData = useRef({});
  const subFormSettingData = useRef({});
  const rdbmsFormSettingData = useRef({});
  const renderFieldsData = useRef({});
  const { formId } = useParams();
  const { setFormsExplorerData, setRdbmsFormsExplorerData } = useTreeContext();
  const [formActvityData, setFormActivityData] = useState({});
  const { layout, layoutData, nestedGridData } = useAppBlocksContext();
  // const [staticData, setStaticData] = useState({});
  const [nestedItemId, setNestedItemId] = useState("");
  const [nestedItemName, setNestedItemName] = useState("");

  const [customFuncData, setCustomFuncData] = useState({
    body: ""
  });
  // const [rdbmsQuery, setRdbmsQuery] = useState(null);
  const [rdbmsConfig, setRdbmsConfig] = useState({
    owner_assignment: {},
    rdbms_query: null,
    rdbms_pre_submission: [],
    rdbms_jobs_trigger_formula: [],
    rdbms_jobs_trigger: []
  });

  const fetchAllForms = async () => {
    try {
      const { data } = await axiosInstance.get(`/state-machines/activities`);
      const { data: customFuncData } = await axiosInstance.get(`/custom_function`);
      const { data: subForms } = await axiosInstance.get(`/subform`);
      // const { data: rdbmsForm } = await axiosInstance.get(`/rdbmsform`);

      // const treeData = getTreeDataForForm(data);
      const treeData = getTreeDataForForm({
        ...data,
        custom_function: customFuncData,
        subform: subForms
        // rdbmsForm: rdbmsForm
      });

      setFormsExplorerData(treeData);
      const statemachine = treeData.find((item) => item.name === "statemachine");
      if (statemachine) {
        statemachine.items.forEach((item) => {
          setNestedItemId(item.id);
          setNestedItemName(item.name);
        });
      }
      return {
        form: data,
        customFunc: customFuncData,
        subForm: subForms,
        treeData: treeData
      };
    } catch (error) {
      handleApiError(error);
    }
  };

  const fetchSqlForms = async () => {
    try {
      // const { data: subForms } = await axiosInstance.get(`/subform`);
      const { data: rdbmsForm } = await axiosInstance.get(`/rdbmsform`);

      // const treeData = getTreeDataForForm(data);
      const treeData = getTreeDataForRdbmsForm({
        // subform: subForms,
        rdbmsForm: rdbmsForm
      });

      setRdbmsFormsExplorerData(treeData);
      const statemachine = treeData.find((item) => item.name === "statemachine");
      if (statemachine) {
        statemachine.items.forEach((item) => {
          setNestedItemId(item.id);
          setNestedItemName(item.name);
        });
      }
      if (selectedForm.id && !formId) {
        // navigate(`./${selectedForm.id}`);
        setSelectedForm({ id: "" });
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const getFormConfig = () => {
    if (selectedForm.type === "subform") {
      const arrayCopy = [...renderFieldsArray];
      arrayCopy.sort((a, b) => a.y - b.y);
      const newArray = arrayCopy.map((field) => {
        return renderFieldsData.current[field.i];
      });
      return {
        fields: newArray,
        settings: subFormSettingData.current.settings,
        desktop_layout: formSectionsData.current[0].desktop_layout,
        mobile_layout: formSectionsData.current[0].mobile_layout
      };
    }
    if (selectedForm.type === "rdbmsform") {
      const arrayCopy = [...renderFieldsArray];
      arrayCopy.sort((a, b) => a.y - b.y);
      const newArray = arrayCopy.map((field) => {
        return renderFieldsData.current[field.i];
      });
      const settings = {
        // ...rdbmsFormSettingData.current,
        name: rdbmsFormSettingData.current.name,
        submit: rdbmsFormSettingData.current.submit,
        reset: rdbmsFormSettingData.current.reset,
        style: rdbmsFormSettingData.current.style || {
          min_width: rdbmsFormSettingData.current.style?.min_width || "",
          width: rdbmsFormSettingData.current.style?.width || "",
          max_width: rdbmsFormSettingData.current.style?.max_width || ""
        }
      };
      return {
        ...rdbmsConfig,
        fields: newArray,
        // rdbms_query: rdbmsQuery,
        settings,
        desktop_layout: formSectionsData.current[0].desktop_layout,
        mobile_layout: formSectionsData.current[0].mobile_layout,
        tablet_layout: formSectionsData.current[0].tablet_layout
      };
    }
    if (selectedForm.type === "custom_function") {
      return customFuncData;
    } else {
      const tempObj = {
        form: {},
        activity: {}
      };

      formSectionsData.current[selectedSectionData].fields = saveCurrentSectionInData(renderFieldsArray);

      tempObj.form = {
        name: formSettingData.current.name,
        sections: formSectionsData.current,
        settings: {
          ...formSettingData.current.settings,
          ignore_local_storage: formSettingData.current.settings.ignore_local_storage
        },
        submit_button: formSettingData.current.submit_button,
        reset_button: formSettingData.current.reset_button,
        style: formSettingData.current.style,
        is_positioned_form: formSettingData.current.is_positioned_form
      };

      tempObj.activity = formDetailviewConfig();
      return tempObj;
    }
  };

  const formDetailviewConfig = () => {
    let { elements } = processLayout(layoutData, nestedGridData, layout);
    return {
      form_backend_validation: formActvityData.form_backend_validation,
      custom_js: formActvityData.custom_js,
      details_view: {
        // ...staticData,
        ...formActvityData.details_view,
        position_config: {
          ...formActvityData.details_view?.position_config,
          desktop: {
            ...formActvityData.details_view?.position_config?.desktop,
            position_config_ids: getElementIds(formActvityData.details_view?.position_config?.desktop?.config || [])
          },
          mobile: {
            ...formActvityData.details_view?.position_config?.mobile,
            position_config_ids: getElementIds(formActvityData.details_view?.position_config?.mobile?.config || [])
          }
        },
        elements: selectedForm.type !== "entities" ? elements || [] : []
      }
    };
  };
  const handleSaveForm = async () => {
    const config = getFormConfig();
    let newMessage;
    try {
      if (selectedForm.type === "custom_function") {
        const { message } = await axiosInstance.patch(`/${selectedForm.type}/${selectedForm.id}`, config);
        newMessage = message;
      } else if (selectedForm.type === "subform") {
        const { message } = await axiosInstance.patch(`/${selectedForm.type}/${selectedForm.id}`, { config: config });
        newMessage = message;
      } else if (selectedForm.type === "rdbmsform") {
        const { message } = await axiosInstance.patch(`/${selectedForm.type}/${selectedForm.id}`, { config: config });
        newMessage = message;
      } else {
        const stateId = selectedForm.parentData.split("/")[0];
        await axiosInstance.put(`/state-machine/${stateId}/activities/${selectedForm.id}`, config.activity);
        const { message } = await axiosInstance.put(
          `/state-machine/${stateId}/activity/${selectedForm.id}/form`,
          config.form
        );
        newMessage = message;
        updateFormChecksum({ ...config.activity, form: config.form });
      }
      // const newChecksum = createChecksum(config);
      // currentChecksum.current = newChecksum;
      return { data: config, message: newMessage };
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };

  const saveCurrentSectionInData = (data = renderFieldsArray) => {
    const arrayCopy = [...data];
    arrayCopy.sort((a, b) => a.y - b.y);

    return arrayCopy.map((field) => {
      return renderFieldsData.current[field.i];
    });
  };

  const payloadStateList = [
    formSettingData.current,
    renderFieldsArray,
    renderFieldsData.current,
    formSectionsData.current,
    formActvityData,
    // staticData,
    formActvityData?.details_view,
    layout,
    layoutData.current,
    nestedGridData.current
  ];

  return (
    <FormContextApi.Provider
      value={{
        renderFieldsData,
        renderFieldsArray,
        formSectionsData,
        formSettingData,
        subFormSettingData,
        rdbmsFormSettingData,
        selectedSectionData,
        selectedForm,
        formActvityData,
        customFuncData,
        // staticData,
        nestedItemId,
        nestedItemName,
        // rdbmsQuery,
        // setRdbmsQuery,
        rdbmsConfig,
        setRdbmsConfig,
        // setStaticData,
        setCustomFuncData,
        getFormConfig,
        handleSaveForm,
        setSelectedForm,
        setRenderFieldsArray,
        setSelectedSectionData,
        saveCurrentSectionInData,
        fetchAllForms,
        setFormActivityData,
        fetchSqlForms,
        payloadStateList
      }}
    >
      {children}
    </FormContextApi.Provider>
  );
};

export default FormContext;
export { useFormContext };
// function assignNewObjectIds(array) {
//   return array.map((item) => ({
//     ...item,
//     id: generateObjectId()
//   }));
// }
